import * as React from 'react'

import { styled, Text, spacing, mq } from '@thg-commerce/gravity-theme'
import {
  SafeHtml,
  Button,
  ProductSpecialOfferBadge,
  Image,
} from '@thg-commerce/gravity-elements'

export interface Images {
  largeProduct: string
}

export interface FreeGiftProduct {
  images: Images[]
  title: string
  price: {
    rrp: {
      displayValue: string
    }
  }
}

export interface MarketedSpecialOfferProps {
  title?: string
  description?: string
  cta?: {
    text: string
    href: string
    openExternally?: boolean
    noFollow?: boolean
  }
  freeGiftProduct?: FreeGiftProduct
  imageWidth: number
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse'
  i18nFreeGiftMessage?: string
  showIcon?: boolean
  onBadgeClick?: (event?: React.MouseEvent) => void
  onlyDisplayOfferBadge?: boolean
  className?: string
}

const Description = styled(SafeHtml)`
  ${Text('bodyText', 'default')}
  margin-top: ${spacing(2)};
  margin-bottom: ${spacing(3)};
  color: ${(props) => props.theme.colors.palette.greys.dark};

  p {
    color: inherit;
  }
`
const StyledImage = styled(Image)`
  margin-bottom: ${spacing(3)};
`
// Need to convert border-color to artemis color value
// once they add support for PAP
const Container = styled.div<{
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse'
  onlyDisplayOfferBadge?: boolean
}>`
  display: flex;
  flex-direction: column;

  ${(props) =>
    props.onlyDisplayOfferBadge &&
    `
      border: 1px solid ${props.theme.colors.system.pap.border};
      margin-bottom: ${spacing(2)};
    `}

  ${(props) =>
    props.direction === 'row' &&
    `
      ${mq(props.theme.breakpointUtils.map, 'sm')} {
        flex-direction: row;
      }
    `}
`

const FreeGiftTitle = styled.div`
  ${Text('bodyText', 'alternate')}
  margin-top: ${spacing(1)};
  margin-bottom: ${spacing(1)};
`

const FreeGiftPrices = styled.div`
  ${Text('bodyText', 'alternate')}
  margin-top: ${spacing(1)};
  margin-bottom: ${spacing(3)};
`

export const MarketedSpecialOffer = (props: MarketedSpecialOfferProps) => {
  return (
    <Container
      className={props.className}
      direction={props.direction}
      data-testid="marketed-special-offer"
      onlyDisplayOfferBadge={props.onlyDisplayOfferBadge}
    >
      {props.freeGiftProduct && !props.onlyDisplayOfferBadge && (
        <div>
          <StyledImage
            src={[
              {
                url: props.freeGiftProduct?.images[0]?.largeProduct,
                width: props.imageWidth,
              },
            ]}
            isAmp={false}
            alt=""
            width={props.imageWidth}
            height={props.imageWidth}
          />
        </div>
      )}
      <div>
        <ProductSpecialOfferBadge
          addPadding={props.onlyDisplayOfferBadge}
          text={props.title || ''}
          showGiftsIcon={props.showIcon}
          onClick={props.onBadgeClick}
          removeTextDecoration={props.onlyDisplayOfferBadge}
        />
        {!props.onlyDisplayOfferBadge && (
          <React.Fragment>
            {props.freeGiftProduct?.title && (
              <FreeGiftTitle>{props.freeGiftProduct.title}</FreeGiftTitle>
            )}
            {props.freeGiftProduct?.price?.rrp?.displayValue && (
              <FreeGiftPrices data-testid="marketed-special-offer-gift-price">
                {`${props.i18nFreeGiftMessage}${props.freeGiftProduct.price.rrp.displayValue}`}
              </FreeGiftPrices>
            )}
            {props.description && (
              <Description
                content={props.description}
                removeTagsAndContent={false}
              />
            )}
            {props.cta && (
              <Button
                sizing="regular"
                emphasis="medium"
                renderedAs="a"
                href={props.cta.href}
                rel={props.cta.noFollow ? 'nofollow' : undefined}
                target={props.cta.openExternally ? '_blank' : undefined}
              >
                {props.cta.text}
              </Button>
            )}
          </React.Fragment>
        )}
      </div>
    </Container>
  )
}
